import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaComment,
  FaTimesCircle,
  FaCheck,
  FaMapMarkerAlt,
  FaRulerHorizontal,
  FaMap,
  FaPause,
} from "react-icons/fa";
import Map, { Marker, Source, Layer } from "react-map-gl/mapbox";
import Header from "../../components/Header";
import ReportOrderModal from "../../components/ReportOrderModal";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import { getUserLocation, getCurrentLocation } from "../../utils/locationUtils";
import { getImageUrl, getProfileImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import "./OrderTracking.css";
import "mapbox-gl/dist/mapbox-gl.css";

// Mapbox Access Token from environment variable
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

// Format distance for display
const formatDistance = (distanceInKm) => {
  if (distanceInKm < 1) {
    return `${Math.round(distanceInKm * 1000)} m`;
  }
  return `${distanceInKm.toFixed(2)} km`;
};

// Farmer considered stopped if location not updated in 45+ seconds
const FARMER_LOCATION_STALE_MS = 45000;

const OrderTracking = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setUser] = useState(null);

  // Map state - will be set based on actual locations
  const [viewState, setViewState] = useState(null);
  const [customerLocation, setCustomerLocation] = useState(null); // Customer's current location
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [paying, setPaying] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [confirmingReceived, setConfirmingReceived] = useState(false);
  const [activeMarkerPopover, setActiveMarkerPopover] = useState(null); // 'farmer' | 'consumer' | null
  const { showToast, showConfirm } = usePopup();

  // Refs for location tracking
  const watchPositionIdRef = useRef(null);
  const locationIntervalRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    fetchOrderDetails();

    // Poll for order updates to get farmer location when shared, resumed, or restarted
    const pollInterval = setInterval(() => {
      fetchOrderDetails(true); // Skip location request on polling updates
    }, 1500); // 1.5 sec for fast resume detection when farmer restarts sharing

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrderDetails = async (skipLocationRequest = false) => {
    if (!skipLocationRequest) {
      setLoading(true);
    }
    try {
      // Fetch order from API
      const response = await api.get(`/orders/${id}`);
      const orderData = response.data;

      // Always request location from browser for accurate delivery tracking (only on initial load)
      let savedUserLocation = null;
      if (!skipLocationRequest) {
        console.log("Order shipping address:", orderData.shippingAddress);

        // Request current location from browser
        if (navigator.geolocation) {
          setRequestingLocation(true);
          setLocationError(null);
          try {
            savedUserLocation = await getCurrentLocation();
            console.log(
              "Location obtained from browser on order tracking page:",
              savedUserLocation,
            );
          } catch (error) {
            console.error("Error getting location:", error);
            // Fallback to saved location if browser request fails
            savedUserLocation = getUserLocation();
            if (!savedUserLocation) {
              setLocationError(t("orderTracking.locationError"));
            }
          } finally {
            setRequestingLocation(false);
          }
        } else {
          // Browser doesn't support geolocation, try saved location
          savedUserLocation = getUserLocation();
          if (!savedUserLocation) {
            setLocationError(t("orderTracking.geolocationNotSupported"));
          }
        }
      } else {
        // For polling updates, use saved location
        savedUserLocation = getUserLocation();
      }

      // Transform order data from backend
      const order = {
        _id: orderData._id,
        orderNumber: orderData._id.slice(-10).toUpperCase(),
        status: orderData.orderStatus || "pending", // Use orderStatus from backend
        createdAt: new Date(orderData.createdAt),
        deliveredAt: orderData.deliveredAt
          ? new Date(orderData.deliveredAt)
          : null,
        estimatedDelivery: orderData.deliveryDate
          ? new Date(orderData.deliveryDate)
          : null,
        farmerLocation: orderData.farmer?.location
          ? {
              latitude: orderData.farmer.location.coordinates[1],
              longitude: orderData.farmer.location.coordinates[0],
              address: `${orderData.farmer.address?.street || ""}, ${orderData.farmer.address?.district || ""}`,
            }
          : null,
        deliveryLocation: (() => {
          // Use user location from browser
          if (savedUserLocation) {
            console.log("Using user location for delivery:", savedUserLocation);
            const deliveryLoc = {
              latitude: savedUserLocation.latitude,
              longitude: savedUserLocation.longitude,
              address: orderData.shippingAddress
                ? `${orderData.shippingAddress.street || ""}, ${orderData.shippingAddress.district || ""}`
                : t("orderTracking.yourLocation"),
            };
            // Also set customerLocation state for continuous tracking
            setCustomerLocation(savedUserLocation);
            return deliveryLoc;
          }
          console.log("No user location available");
          return null;
        })(),
        items:
          orderData.items?.map((item) => {
            const product = item.product || {};
            const imageUrl = product.images?.[0]
              ? product.images[0].startsWith("http")
                ? product.images[0]
                : getImageUrl(product.images[0])
              : null;

            return {
              _id: item.product?._id || item.product || item._id,
              product: {
                _id: product._id || item.product?._id || item.product,
                name: item.name || product.name || "Product",
                price: item.price || product.price || 0,
                unit: product.unit || "kg",
                image: imageUrl,
              },
              quantity: item.quantity || 0,
              subtotal: (item.price || 0) * (item.quantity || 0),
            };
          }) || [],
        delivery: {
          method: orderData.shippingAddress?.deliveryMethod || "home",
          address: orderData.shippingAddress
            ? `${orderData.shippingAddress.street || ""}, ${orderData.shippingAddress.district || ""}`
            : "N/A",
          city: orderData.shippingAddress?.city || "",
          district: orderData.shippingAddress?.district || "",
          province: orderData.shippingAddress?.province || "",
          phone:
            orderData.shippingAddress?.phone || orderData.consumer?.phone || "",
        },
        payment: {
          method: orderData.paymentMethod || "COD",
          status: orderData.paymentStatus || "pending",
          amount: orderData.totalPrice || 0,
          gateway: orderData.paymentGateway || null,
        },
        farmer: {
          _id: orderData.farmer?._id || orderData.farmer,
          name: orderData.farmer?.name || "Farmer",
          username: orderData.farmer?.username || null,
          district: orderData.farmer?.address?.district || "",
          phone: orderData.farmer?.phone || "",
          profileImage: orderData.farmer?.profileImage || null,
          address: orderData.farmer?.address || null,
          locationLastUpdated: orderData.farmer?.locationLastUpdated || null,
        },
        consumer: (() => {
          const c = orderData.consumer;
          if (c)
            return {
              _id: c._id || c,
              name: c.name || "You",
              username: c.username || null,
              profileImage: c.profileImage || null,
            };
          try {
            const u = JSON.parse(localStorage.getItem("user") || "null");
            return u
              ? {
                  _id: u._id,
                  name: u.name || "You",
                  username: u.username || null,
                  profileImage: u.profileImage || null,
                }
              : null;
          } catch {
            return null;
          }
        })(),
        tracking: [
          {
            status: "pending",
            message: t("orderTracking.orderPlaced"),
            timestamp: new Date(orderData.createdAt),
            completed: [
              "pending",
              "confirmed",
              "processing",
              "shipped",
              "delivered",
              "received",
              "completed",
            ].includes(orderData.orderStatus),
          },
          {
            status: "confirmed",
            message: t("orderTracking.orderConfirmed"),
            timestamp: orderData.confirmedAt
              ? new Date(orderData.confirmedAt)
              : null,
            completed: [
              "confirmed",
              "processing",
              "shipped",
              "delivered",
              "received",
              "completed",
            ].includes(orderData.orderStatus),
          },
          {
            status: "processing",
            message: t("orderTracking.preparingOrder"),
            timestamp: orderData.processingAt
              ? new Date(orderData.processingAt)
              : null,
            completed: [
              "processing",
              "shipped",
              "delivered",
              "received",
              "completed",
            ].includes(orderData.orderStatus),
          },
          {
            status: "shipped",
            message: t("orderTracking.outForDelivery"),
            timestamp: orderData.shippedAt
              ? new Date(orderData.shippedAt)
              : null,
            completed: [
              "shipped",
              "delivered",
              "received",
              "completed",
            ].includes(orderData.orderStatus),
          },
          {
            status: "delivered",
            message: t("orderTracking.orderDelivered"),
            timestamp: orderData.deliveredAt
              ? new Date(orderData.deliveredAt)
              : null,
            completed: ["delivered", "received", "completed"].includes(
              orderData.orderStatus,
            ),
          },
          {
            status: "received",
            message: t("orderTracking.orderReceived"),
            timestamp: orderData.receivedAt
              ? new Date(orderData.receivedAt)
              : null,
            completed: ["received", "completed"].includes(
              orderData.orderStatus,
            ),
          },
        ],
      };

      // Update order state
      setOrder(order);

      console.log("Order data from backend:", {
        orderData: orderData,
        farmer: orderData.farmer,
        farmerLocation: orderData.farmer?.location,
        farmerLocationCoordinates: orderData.farmer?.location?.coordinates,
      });

      console.log("Processed order locations:", {
        farmerLocation: order.farmerLocation,
        deliveryLocation: order.deliveryLocation,
        savedUserLocation: savedUserLocation,
        hasMapboxToken: !!MAPBOX_TOKEN,
        deliveryLat: order.deliveryLocation?.latitude,
        deliveryLng: order.deliveryLocation?.longitude,
        farmerLat: order.farmerLocation?.latitude,
        farmerLng: order.farmerLocation?.longitude,
        viewStateWillBeSet: !!(order.farmerLocation || order.deliveryLocation),
      });

      // Set map view only on initial load (not on polling) - preserves user zoom/pan
      const customerLoc = customerLocation || order.deliveryLocation;
      if (!skipLocationRequest && (order.farmerLocation || customerLoc)) {
        if (order.farmerLocation && customerLoc) {
          const centerLat =
            (order.farmerLocation.latitude + customerLoc.latitude) / 2;
          const centerLng =
            (order.farmerLocation.longitude + customerLoc.longitude) / 2;
          const distance = calculateDistance(
            order.farmerLocation.latitude,
            order.farmerLocation.longitude,
            customerLoc.latitude,
            customerLoc.longitude,
          );
          let zoom = 12;
          if (distance > 50) zoom = 9;
          else if (distance > 20) zoom = 10;
          else if (distance > 10) zoom = 11;
          else if (distance > 5) zoom = 12;
          else zoom = 13;
          setViewState({
            latitude: centerLat,
            longitude: centerLng,
            zoom: zoom,
          });
        } else if (order.farmerLocation) {
          setViewState({
            latitude: order.farmerLocation.latitude,
            longitude: order.farmerLocation.longitude,
            zoom: 13,
          });
        } else if (customerLoc) {
          setViewState({
            latitude: customerLoc.latitude,
            longitude: customerLoc.longitude,
            zoom: 13,
          });
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching order:", error);
      setLoading(false);
    }
  };

  // Start continuous location tracking for customer
  useEffect(() => {
    if (navigator.geolocation && order) {
      // Start watching position for continuous updates
      watchPositionIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setCustomerLocation(newLocation);
          // Update order's deliveryLocation with current location
          if (order) {
            setOrder((prev) => ({
              ...prev,
              deliveryLocation: {
                ...prev.deliveryLocation,
                latitude: newLocation.latitude,
                longitude: newLocation.longitude,
              },
            }));
          }
        },
        (error) => {
          console.error("Error watching location:", error);
          // Don't show error if user just hasn't granted permission yet
          if (error.code !== 1) {
            console.warn("Location tracking error:", error);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );

      // Also update location every 5 seconds as backup
      locationIntervalRef.current = setInterval(async () => {
        try {
          const backupLocation = await getCurrentLocation();
          setCustomerLocation(backupLocation);
          if (order) {
            setOrder((prev) => ({
              ...prev,
              deliveryLocation: {
                ...prev.deliveryLocation,
                latitude: backupLocation.latitude,
                longitude: backupLocation.longitude,
              },
            }));
          }
        } catch (error) {
          console.error("Error updating location (backup interval):", error);
        }
      }, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (watchPositionIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchPositionIdRef.current);
        watchPositionIdRef.current = null;
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?._id]); // Only restart when order ID changes

  // Update map center when customer/farmer location changes - preserve user zoom/pan
  useEffect(() => {
    if (order && customerLocation && order.farmerLocation) {
      const centerLat =
        (order.farmerLocation.latitude + customerLocation.latitude) / 2;
      const centerLng =
        (order.farmerLocation.longitude + customerLocation.longitude) / 2;
      setViewState((prev) => ({
        ...prev,
        latitude: centerLat,
        longitude: centerLng,
      }));
    } else if (order && customerLocation && !order.farmerLocation) {
      setViewState((prev) => ({
        ...prev,
        latitude: customerLocation.latitude,
        longitude: customerLocation.longitude,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerLocation, order?.farmerLocation]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "#FF9800",
      confirmed: "#2196F3",
      processing: "#9C27B0",
      shipped: "#00BCD4",
      outForDelivery: "#00BCD4",
      delivered: "#4CAF50",
      received: "#0d9488",
      completed: "#15803d",
      cancelled: "#F44336",
    };
    return colors[status] || "#666";
  };

  if (loading) {
    return (
      <div className="order-tracking-page">
        <Header />
        <div className="order-tracking-loading-state">
          <div className="order-tracking-spinner"></div>
          <p>{t("orderTracking.loading")}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-tracking-page">
        <Header />
        <div className="order-tracking-empty-state">
          <h2>{t("orderTracking.orderNotFound")}</h2>
          <Link to="/orders" className="order-tracking-btn-back">
            {t("orderTracking.backToOrders")}
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = order.items.reduce((sum, item) => sum + item.subtotal, 0);
  const deliveryFee =
    order.delivery.method === "home" ? (subtotal > 500 ? 0 : 50) : 0;
  // Use totalPrice from backend if available, otherwise calculate
  const total = order.payment.amount || subtotal + deliveryFee;

  let canConsumerCancelHere = false;
  try {
    const me = JSON.parse(localStorage.getItem("user") || "null");
    const uid = me?.id || me?._id;
    const cid = order.consumer?._id;
    canConsumerCancelHere =
      me?.role === "consumer" &&
      uid &&
      cid &&
      String(uid) === String(cid) &&
      order.status === "pending" &&
      order.payment?.status !== "paid";
  } catch {
    canConsumerCancelHere = false;
  }

  return (
    <div className="order-tracking-page">
      <Header />

      <div className="order-tracking-container">
        <div className="order-tracking-order-header">
          <div>
            <h1 className="order-tracking-order-title">
              {t("orderTracking.title")}
            </h1>
            <p
              className="order-tracking-order-number"
              style={{ color: "#FFFFFF" }}
            >
              {t("orderTracking.orderNumber", {
                orderNumber: toLocalizedNumber(order.orderNumber),
              })}
            </p>
          </div>
          <div
            className="order-tracking-order-status-badge"
            style={{ background: getStatusColor(order.status) }}
          >
            {toLocalizedStatus(order.status, t)}
          </div>
        </div>

        {/* Order Timeline */}
        <div className="order-tracking-timeline-section">
          <h2 className="order-tracking-section-title">
            {t("orderTracking.orderStatus")}
          </h2>
          <div className="order-tracking-timeline">
            {order.tracking.map((track, index) => {
              const isLast = index === order.tracking.length - 1;
              return (
                <React.Fragment key={index}>
                  <div
                    className={`order-tracking-timeline-item ${track.completed ? "completed" : ""}`}
                  >
                    <div
                      className="order-tracking-timeline-marker"
                      style={{
                        background: track.completed
                          ? getStatusColor(track.status)
                          : "#E0E0E0",
                      }}
                    >
                      {track.completed ? <FaCheck aria-hidden /> : null}
                    </div>
                    <div className="order-tracking-timeline-content">
                      <div className="order-tracking-timeline-status">
                        {toLocalizedStatus(track.status, t)}
                      </div>
                      <div className="order-tracking-timeline-message">
                        {track.message}
                      </div>
                      <div className="order-tracking-timeline-time">
                        {track.timestamp
                          ? new Date(track.timestamp).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </div>
                    </div>
                  </div>
                  {!isLast && (
                    <div className="order-tracking-timeline-connector">
                      <div
                        className="order-tracking-timeline-connector-line"
                        style={{
                          background: track.completed
                            ? getStatusColor(track.status)
                            : "#E0E0E0",
                        }}
                      ></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Live Tracking Map - Always show */}
        <div className="order-tracking-map-section">
          <h2 className="order-tracking-section-title">
            {t("orderTracking.realTimeTracking")}
          </h2>
          {requestingLocation && (
            <div className="order-tracking-location-request-message">
              <div
                className="order-tracking-location-request-spinner"
                aria-hidden
              >
                <FaMapMarkerAlt />
              </div>
              <p>{t("orderTracking.requestingLocation")}</p>
            </div>
          )}
          {locationError && (
            <div className="order-tracking-location-error-message">
              <p>{locationError}</p>
            </div>
          )}
          {(order.farmerLocation ||
            customerLocation ||
            order.deliveryLocation) &&
          viewState &&
          typeof viewState.latitude === "number" &&
          typeof viewState.longitude === "number" &&
          viewState.zoom &&
          MAPBOX_TOKEN &&
          MAPBOX_TOKEN.trim() !== "" ? (
            <div className="order-tracking-map-container">
              {/* Farmer stopped sharing - show message and last checkpoint */}
              {(order.status === "shipped" || order.status === "delivered") &&
                order.farmerLocation &&
                (() => {
                  const lastUpdated = order.farmer?.locationLastUpdated
                    ? new Date(order.farmer.locationLastUpdated).getTime()
                    : 0;
                  const isStale =
                    !lastUpdated ||
                    Date.now() - lastUpdated > FARMER_LOCATION_STALE_MS;
                  if (!isStale) return null;
                  const addr = order.farmer?.address;
                  const lastCheckpointAddr = addr
                    ? [addr.street, addr.district, addr.city, addr.province]
                        .filter(Boolean)
                        .join(", ") || t("orderTracking.addressNotSet")
                    : t("orderTracking.addressNotSet");
                  return (
                    <div className="order-tracking-farmer-stopped-message">
                      <span
                        className="order-tracking-farmer-stopped-icon"
                        aria-hidden
                      >
                        <FaPause />
                      </span>
                      <div>
                        <strong>
                          {t("orderTracking.farmerStoppedSharing")}
                        </strong>
                        <span className="order-tracking-farmer-stopped-last">
                          {" "}
                          {t("orderTracking.lastShared", {
                            address: lastCheckpointAddr,
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              <Map
                ref={mapRef}
                {...viewState}
                onMove={(evt) => setViewState(evt.viewState)}
                mapboxAccessToken={MAPBOX_TOKEN}
                style={{ width: "100%", height: "500px", borderRadius: "16px" }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                reuseMaps={true}
                preventStyleDiffing={true}
              >
                {/* Line connecting farmer and customer - only when shipped and farmer has shared location */}
                {(order.status === "shipped" || order.status === "delivered") &&
                  order.farmerLocation &&
                  (customerLocation || order.deliveryLocation) && (
                    <Source
                      id="route-line"
                      type="geojson"
                      data={{
                        type: "Feature",
                        properties: {},
                        geometry: {
                          type: "LineString",
                          coordinates: [
                            [
                              order.farmerLocation.longitude,
                              order.farmerLocation.latitude,
                            ],
                            [
                              (customerLocation || order.deliveryLocation)
                                .longitude,
                              (customerLocation || order.deliveryLocation)
                                .latitude,
                            ],
                          ],
                        },
                      }}
                    >
                      <Layer
                        id="route-line-layer"
                        type="line"
                        layout={{
                          "line-join": "round",
                          "line-cap": "round",
                        }}
                        paint={{
                          "line-color": "#4CAF50",
                          "line-width": 3,
                          "line-dasharray": [2, 2],
                        }}
                      />
                    </Source>
                  )}

                {/* Farmer Location Marker - only when shipped and farmer shares location; profile avatar, click to show popover */}
                {(order.status === "shipped" || order.status === "delivered") &&
                  order.farmerLocation &&
                  (() => {
                    const lastUpdated = order.farmer?.locationLastUpdated
                      ? new Date(order.farmer.locationLastUpdated).getTime()
                      : 0;
                    const farmerHasStopped =
                      !lastUpdated ||
                      Date.now() - lastUpdated > FARMER_LOCATION_STALE_MS;
                    return (
                      <Marker
                        latitude={order.farmerLocation.latitude}
                        longitude={order.farmerLocation.longitude}
                        anchor="bottom"
                      >
                        <div
                          className={`order-tracking-map-marker-wrapper ${farmerHasStopped ? "order-tracking-marker-last-shared" : ""}`}
                        >
                          <div
                            className="order-tracking-farmer-marker"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMarkerPopover(
                                activeMarkerPopover === "farmer"
                                  ? null
                                  : "farmer",
                              );
                            }}
                          >
                            <div className="order-tracking-marker-icon order-tracking-farmer-avatar-wrap">
                              {order.farmer?.profileImage ? (
                                <img
                                  src={
                                    order.farmer.profileImage.startsWith("http")
                                      ? order.farmer.profileImage
                                      : getProfileImageUrl(
                                          order.farmer.profileImage,
                                        )
                                  }
                                  alt={order.farmer.name}
                                  className="order-tracking-farmer-avatar"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(order.farmer?.name || "Farmer")}&background=4CAF50&color=fff&size=80`;
                                  }}
                                />
                              ) : (
                                <div className="order-tracking-marker-initials">
                                  {order.farmer?.name
                                    ?.charAt(0)
                                    ?.toUpperCase() || "F"}
                                </div>
                              )}
                            </div>
                            <div className="order-tracking-marker-label">
                              {farmerHasStopped
                                ? "Last shared"
                                : order.farmer?.name || "Farmer"}
                            </div>
                          </div>
                          {activeMarkerPopover === "farmer" &&
                            (() => {
                              const lastUpdated = order.farmer
                                ?.locationLastUpdated
                                ? new Date(
                                    order.farmer.locationLastUpdated,
                                  ).getTime()
                                : 0;
                              const farmerHasStopped =
                                !lastUpdated ||
                                Date.now() - lastUpdated >
                                  FARMER_LOCATION_STALE_MS;
                              const addr = order.farmer?.address;
                              const lastCheckpointAddr = addr
                                ? [
                                    addr.street,
                                    addr.district,
                                    addr.city,
                                    addr.province,
                                  ]
                                    .filter(Boolean)
                                    .join(", ") ||
                                  t("orderTracking.addressNotSet")
                                : t("orderTracking.addressNotSet");
                              return (
                                <div
                                  className="order-tracking-marker-popover"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {farmerHasStopped ? (
                                    <div className="order-tracking-popover-name">
                                      {t("orderTracking.lastShared", {
                                        address: lastCheckpointAddr,
                                      })}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="order-tracking-popover-name">
                                        {order.farmer?.name || "Farmer"}
                                      </div>
                                      <div className="order-tracking-popover-links">
                                        <Link
                                          to={`/users/${order.farmer?.username || order.farmer?._id || ""}`}
                                          className="order-tracking-popover-link"
                                          title={t("orderTracking.profile")}
                                        >
                                          <FaUser />{" "}
                                          {t("orderTracking.profile")}
                                        </Link>
                                        <Link
                                          to={`/messages?farmer=${order.farmer?._id || ""}`}
                                          className="order-tracking-popover-link"
                                          title={t("orderTracking.message")}
                                        >
                                          <FaComment />{" "}
                                          {t("orderTracking.message")}
                                        </Link>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                        </div>
                      </Marker>
                    );
                  })()}

                {/* Customer Location Marker (Your Location) - profile avatar, click to show popover */}
                {(customerLocation || order.deliveryLocation) && (
                  <Marker
                    latitude={
                      (customerLocation || order.deliveryLocation).latitude
                    }
                    longitude={
                      (customerLocation || order.deliveryLocation).longitude
                    }
                    anchor="bottom"
                  >
                    <div className="order-tracking-map-marker-wrapper">
                      <div
                        className="order-tracking-delivery-marker"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMarkerPopover(
                            activeMarkerPopover === "consumer"
                              ? null
                              : "consumer",
                          );
                        }}
                      >
                        <div className="order-tracking-marker-icon order-tracking-farmer-avatar-wrap">
                          {order.consumer?.profileImage ? (
                            <img
                              src={
                                order.consumer.profileImage.startsWith("http")
                                  ? order.consumer.profileImage
                                  : getProfileImageUrl(
                                      order.consumer.profileImage,
                                    )
                              }
                              alt={order.consumer?.name || "You"}
                              className="order-tracking-farmer-avatar"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(order.consumer?.name || "You")}&background=2196F3&color=fff&size=80`;
                              }}
                            />
                          ) : (
                            <div className="order-tracking-marker-initials">
                              {(order.consumer?.name || "You")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>
                      {activeMarkerPopover === "consumer" &&
                        (() => {
                          const addr = order.delivery?.address;
                          const locationText =
                            addr && addr !== "N/A" && addr.trim()
                              ? addr
                              : [
                                  order.delivery?.district,
                                  order.delivery?.city,
                                  order.delivery?.province,
                                ]
                                  .filter(Boolean)
                                  .join(", ") ||
                                order.deliveryLocation?.address ||
                                t("orderTracking.yourLocation");
                          return (
                            <div
                              className="order-tracking-marker-popover"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="order-tracking-popover-name">
                                {locationText}
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </Marker>
                )}
              </Map>

              {/* Distance between you and farmer - shown when shipped and farmer has shared location */}
              {(order.status === "shipped" || order.status === "delivered") &&
                order.farmerLocation &&
                (customerLocation || order.deliveryLocation) &&
                (() => {
                  const customerLoc =
                    customerLocation || order.deliveryLocation;
                  const distance = calculateDistance(
                    order.farmerLocation.latitude,
                    order.farmerLocation.longitude,
                    customerLoc.latitude,
                    customerLoc.longitude,
                  );
                  return (
                    <div className="order-tracking-distance-info">
                      <div className="order-tracking-distance-icon" aria-hidden>
                        <FaRulerHorizontal />
                      </div>
                      <div className="order-tracking-distance-details">
                        <span className="order-tracking-distance-label">
                          {t("orderTracking.distanceToYou")}
                        </span>
                        <span className="order-tracking-distance-value">
                          {toLocalizedNumber(formatDistance(distance))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
            </div>
          ) : !MAPBOX_TOKEN ? (
            <div
              className="order-tracking-map-placeholder"
              style={{
                width: "100%",
                height: "500px",
                borderRadius: "16px",
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "16px",
                border: "2px dashed #ddd",
              }}
            >
              <div className="order-tracking-map-placeholder-icon" aria-hidden>
                <FaMap />
              </div>
              <p style={{ color: "#666", fontSize: "16px" }}>
                {t("orderTracking.mapRequiresToken")}
              </p>
              <p
                style={{ color: "#999", fontSize: "14px", textAlign: "center" }}
              >
                {order?.farmerLocation &&
                  `Farmer: ${order.farmerLocation.address || "Location available"}`}
                {order?.farmerLocation &&
                  (customerLocation || order?.deliveryLocation) && <br />}
                {(customerLocation || order?.deliveryLocation) &&
                  `${t("orderTracking.yourLocation")}: ${(customerLocation || order?.deliveryLocation).address || "Location available"}`}
              </p>
              <p style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>
                {t("orderTracking.mapTokenHint")}
              </p>
            </div>
          ) : (
            <div
              className="map-placeholder"
              style={{
                width: "100%",
                height: "500px",
                borderRadius: "16px",
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "16px",
                border: "2px dashed #ddd",
              }}
            >
              <div className="order-tracking-map-placeholder-icon" aria-hidden>
                <FaMapMarkerAlt />
              </div>
              <p style={{ color: "#666", fontSize: "16px" }}>
                {t("orderTracking.locationNotAvailable")}
              </p>
              <p style={{ color: "#999", fontSize: "14px" }}>
                {t("orderTracking.waitingForLocation")}
              </p>
            </div>
          )}
        </div>

        <div className="order-tracking-order-details-grid">
          {/* Order Items */}
          <div className="order-tracking-order-items-section">
            <h2 className="order-tracking-section-title">
              {t("orderTracking.orderItems")}
            </h2>
            <div className="order-tracking-order-items-list">
              {order.items.map((item) => (
                <div key={item._id} className="order-tracking-order-item-card">
                  <Link
                    to={`/products/${item.product._id}`}
                    className="order-tracking-order-item-image"
                  >
                    {item.product.image &&
                    (item.product.image.startsWith("http") ||
                      item.product.image.startsWith("/")) ? (
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://via.placeholder.com/400x400/E8F5E9/4CAF50?text=${encodeURIComponent(item.product.name)}`;
                        }}
                      />
                    ) : (
                      <img
                        src={
                          item.product.image ||
                          "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=400&h=400&fit=crop"
                        }
                        alt={item.product.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://via.placeholder.com/400x400/E8F5E9/4CAF50?text=${encodeURIComponent(item.product.name)}`;
                        }}
                      />
                    )}
                  </Link>
                  <div className="order-tracking-order-item-info">
                    <Link
                      to={`/products/${item.product._id}`}
                      className="order-tracking-item-name-link"
                    >
                      <h3 className="order-tracking-item-name">
                        {item.product.name}
                      </h3>
                    </Link>
                    <div className="order-tracking-item-meta">
                      <span>
                        {t("orderTracking.quantity", {
                          quantity: toLocalizedNumber(item.quantity),
                          unit: item.product.unit,
                        })}
                      </span>
                      <span>
                        {t("orderTracking.price", {
                          price: toLocalizedPrice(item.product.price),
                          unit: item.product.unit,
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="order-tracking-order-item-subtotal">
                    Rs. {toLocalizedPrice(item.subtotal)}
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="order-tracking-order-summary-card">
              <h3 className="order-tracking-summary-title">
                {t("orderTracking.orderSummary")}
              </h3>
              <div className="order-tracking-summary-row">
                <span>{t("orderTracking.subtotal")}</span>
                <span>Rs. {toLocalizedPrice(subtotal)}</span>
              </div>
              <div className="order-tracking-summary-row">
                <span>{t("orderTracking.deliveryFee")}</span>
                <span>
                  {deliveryFee === 0
                    ? "Free"
                    : `Rs. ${toLocalizedPrice(deliveryFee)}`}
                </span>
              </div>
              <div className="order-tracking-summary-divider"></div>
              <div className="order-tracking-summary-total">
                <span>{t("orderTracking.total")}</span>
                <span>Rs. {toLocalizedPrice(total)}</span>
              </div>
            </div>
          </div>

          {/* Order Information */}
          <div className="order-tracking-order-info-section">
            <div className="order-tracking-info-card">
              <h3 className="order-tracking-info-title">
                {t("orderTracking.orderInformation")}
              </h3>
              <div className="order-tracking-info-content">
                {/* Delivery Information */}
                <div className="order-tracking-info-group">
                  <h4 className="order-tracking-info-group-title">
                    {t("orderTracking.deliveryInformation")}
                  </h4>
                  <p>
                    <strong>{t("orderTracking.method")}:</strong>{" "}
                    {order.delivery.method === "home"
                      ? t("orderTracking.homeDelivery")
                      : t("orderTracking.pickupFromFarm")}
                  </p>
                  <p>
                    <strong>{t("orderTracking.address")}:</strong>{" "}
                    {order.delivery.address}
                  </p>
                  <p>
                    <strong>{t("orderTracking.city")}:</strong>{" "}
                    {order.delivery.city}
                  </p>
                  <p>
                    <strong>{t("orderTracking.district")}:</strong>{" "}
                    {order.delivery.district}
                  </p>
                  <p>
                    <strong>{t("orderTracking.phone")}:</strong>{" "}
                    {order.delivery.phone}
                  </p>
                  {order.estimatedDelivery && (
                    <p>
                      <strong>{t("orderTracking.estimatedDelivery")}:</strong>{" "}
                      {new Date(order.estimatedDelivery).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="order-tracking-info-divider"></div>

                {/* Payment Information */}
                <div className="order-tracking-info-group">
                  <h4 className="order-tracking-info-group-title">
                    {t("orderTracking.paymentInformation")}
                  </h4>
                  <p>
                    <strong>{t("orderTracking.paymentMethod")}:</strong>{" "}
                    {order.payment.method === "COD" ||
                    order.payment.method === "cod"
                      ? t("orderTracking.cashOnDelivery")
                      : t("orderTracking.onlinePayment")}
                  </p>
                  <p>
                    <strong>{t("orderTracking.paymentStatus")}:</strong>{" "}
                    <span
                      className={`order-tracking-payment-status ${order.payment.status}`}
                    >
                      {toLocalizedStatus(order.payment.status, t)}
                    </span>
                  </p>
                  <p>
                    <strong>{t("orderTracking.amount")}:</strong> Rs.{" "}
                    {toLocalizedPrice(order.payment.amount)}
                  </p>
                  {order.payment.method === "COD" &&
                    order.payment.status === "pending" && (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginTop: "8px",
                        }}
                      >
                        {t("orderTracking.paymentOnDelivery")}
                      </p>
                    )}
                  {(order.payment.method === "online" ||
                    order.payment.method === "Online") &&
                    order.payment.status !== "paid" && (
                      <button
                        type="button"
                        className={`order-tracking-btn-pay-again ${order.payment.gateway === "esewa" ? "order-tracking-btn-esewa" : "order-tracking-btn-khalti"}`}
                        onClick={async () => {
                          setPaying(true);
                          try {
                            if (order.payment.gateway === "esewa") {
                              const res = await api.post(
                                "/payments/esewa/initiate",
                                { orderId: order._id },
                              );
                              const { esewaFormData } = res.data;
                              if (esewaFormData) {
                                const form = document.createElement("form");
                                form.method = "POST";
                                form.action = esewaFormData.url;
                                const { url, ...fields } = esewaFormData;
                                Object.entries(fields).forEach(
                                  ([key, value]) => {
                                    const input =
                                      document.createElement("input");
                                    input.type = "hidden";
                                    input.name = key;
                                    input.value = value;
                                    form.appendChild(input);
                                  },
                                );
                                document.body.appendChild(form);
                                form.submit();
                                return;
                              }
                            } else {
                              const res = await api.post(
                                "/payments/khalti/initiate",
                                { orderId: order._id },
                              );
                              if (res.data?.payment_url) {
                                window.location.href = res.data.payment_url;
                                return;
                              }
                            }
                            throw new Error("No payment URL");
                          } catch (err) {
                            setPaying(false);
                            showToast(
                              err.response?.data?.message ||
                                t("orderTracking.paymentError"),
                              "error",
                            );
                          }
                        }}
                        disabled={paying}
                      >
                        {paying
                          ? t("orderTracking.redirecting")
                          : t("orderTracking.payAgain")}
                      </button>
                    )}
                </div>

                <div className="order-tracking-info-divider"></div>

                {/* Farmer Information */}
                <div className="order-tracking-info-group">
                  <h4 className="order-tracking-info-group-title">
                    {t("orderTracking.farmerInformation")}
                  </h4>
                  <p>
                    <strong>{t("orderTracking.farmerName")}:</strong>{" "}
                    <Link
                      to={`/users/${order.farmer.username || order.farmer._id || "1"}`}
                      className="order-tracking-farmer-link"
                    >
                      {order.farmer.name}
                    </Link>
                  </p>
                  <p>
                    <strong>{t("orderTracking.farmerDistrict")}:</strong>{" "}
                    {order.farmer.district}
                  </p>
                  <p>
                    <strong>{t("orderTracking.farmerPhone")}:</strong>{" "}
                    {order.farmer.phone}
                  </p>
                </div>
              </div>
            </div>

            <div className="order-tracking-order-actions">
              <Link to="/orders" className="order-tracking-btn-back-orders">
                {t("orderTracking.backToOrders")}
              </Link>
              {order.status === "delivered" && (
                <button
                  type="button"
                  className="order-tracking-btn-received"
                  disabled={confirmingReceived}
                  onClick={() => {
                    showConfirm({
                      title: t("myOrders.confirmReceiveTitle"),
                      message: t("myOrders.confirmReceiveMessage"),
                      confirmLabel: t("common.confirm"),
                      cancelLabel: t("common.cancel"),
                      onConfirm: async () => {
                        setConfirmingReceived(true);
                        try {
                          await api.put(`/orders/${order._id}/received`);
                          showToast(t("myOrders.receivedSuccess"), "success");
                          fetchOrderDetails();
                        } catch (err) {
                          showToast(
                            err.response?.data?.message || "Error",
                            "error",
                          );
                        } finally {
                          setConfirmingReceived(false);
                        }
                      },
                    });
                  }}
                >
                  {confirmingReceived
                    ? t("orderTracking.confirmingReceivedBtn")
                    : t("orderTracking.markReceivedBtn")}
                </button>
              )}
              {canConsumerCancelHere && (
                <button
                  type="button"
                  className="order-tracking-btn-cancel-order"
                  disabled={cancellingOrder}
                  onClick={() => {
                    showConfirm({
                      title: t("myOrders.confirmCancelTitle"),
                      message: t("myOrders.confirmCancelMessage"),
                      confirmLabel: t("common.confirm"),
                      cancelLabel: t("common.cancel"),
                      onConfirm: async () => {
                        setCancellingOrder(true);
                        try {
                          await api.put(`/orders/${order._id}/cancel`);
                          showToast(t("myOrders.cancelSuccess"), "success");
                          fetchOrderDetails(true);
                        } catch (err) {
                          showToast(
                            err.response?.data?.message || "Request failed",
                            "error",
                          );
                        } finally {
                          setCancellingOrder(false);
                        }
                      },
                    });
                  }}
                >
                  <FaTimesCircle style={{ marginRight: 6 }} />
                  {cancellingOrder
                    ? t("myOrders.cancellingOrder")
                    : t("myOrders.cancelOrder")}
                </button>
              )}
              <button
                type="button"
                className="order-tracking-btn-report"
                onClick={() => setShowReportModal(true)}
              >
                {t("orderTracking.reportIssue")}
              </button>
            </div>
            {showReportModal && (
              <ReportOrderModal
                orderId={order._id}
                orderNumber={order.orderNumber}
                onClose={() => setShowReportModal(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
